import { isEqual } from '../helpers/common.js';

function createStore(initial_state = {}, initial_persistence = {}) {
    const listeners = {};

    let state = { ...initial_state };
    let persistence = { ...initial_persistence };
    let persistence_location = 'local';

    function loadPersistence() {
        if (typeof window === 'undefined' || !window.localStorage) return;

        let saved_persistence;

        try {
            saved_persistence = localStorage.getItem('persistence');
            persistence_location = 'local';
        } catch (error) {
            try {
                Object.keys(persistence).forEach(key => {
                    if (persistence[key] === 'local') persistence[key] = 'session';
                });

                saved_persistence = sessionStorage.getItem('persistence');
                persistence_location = 'session';

            } catch (error) {
                console.error('Failed to load persistence from both local and session storage:', error);

                Object.keys(persistence).forEach(key => {
                    persistence[key] = 'memory';
                });
                persistence_location = 'memory';
                return;
            }
        }

        if (saved_persistence) {
            try {
                saved_persistence = JSON.parse(saved_persistence);
            } catch (error) {
                saved_persistence = {};
            }

            persistence = { ...persistence, ...saved_persistence };
        }
    }

    function loadState() {
        Object.keys(persistence).forEach(key => {
            const value = getFromStorage(key);
            if (value !== null && value !== undefined ) {
                state[key] = value;
            }
        })
    }

    function getFromStorage(key, default_value = undefined) {
        if (!persistence[key]) return default_value;

        try {
            if (persistence[key] === 'local') {
                return JSON.parse(localStorage.getItem(key));
            } else if (persistence[key] === 'session') {
                return JSON.parse(sessionStorage.getItem(key));
            }
        } catch (error) {
            console.warn(`Failed to load ${key} from storage:`, error);
        }
    }

    function saveToStorage(key, value) {
        if (!persistence[key]) return;

        try {
            if (persistence[key] === 'local') {
                localStorage.setItem(key, JSON.stringify(value));
            } else if (persistence[key] === 'session') {
                sessionStorage.setItem(key, JSON.stringify(value));
            }
        } catch (error) {
            console.warn(`Failed to save ${key} to ${persistence[key]} storage. Trying another method.`);
            if (persistence[key] === 'local') {
                setPersistence(key, 'session', false);
            } else if (persistence[key] === 'session') {
                setPersistence(key, 'memory', false);
            }

            if (persistence[key] === 'local' || persistence[key] === 'session') {
                saveToStorage(key, value);
            }
        }
    }

    function removeFromStorage(key) {
        if (!persistence[key]) return;

        try {
            if (persistence[key] === 'local') {
                localStorage.removeItem(key);
            } else if (persistence[key] === 'session') {
                sessionStorage.removeItem(key);
            }
        } catch (error) {
            console.warn(`Failed to remove ${key} from storage:`, error);
        }
    }

    function get(key, default_value = undefined) {
        const value = state[key] !== undefined ? state[key] : default_value;
        if (value !== null && typeof value === 'object') {
            return structuredClone(value);
        }
        return value;
    }

    function set(key, value) {
        const oldValue = state[key];

        if (isEqual(oldValue, value)) return;

        state[key] = value;
        saveToStorage(key, value);

        if (listeners[key]) {
            listeners[key].forEach(fn => fn(value));
        }
    }

    function clear(key) {
        state[key] = initial_state[key] ?? null;
        removeFromStorage(key);

        listeners[key]?.forEach(fn => fn(state[key]));
    }

    function subscribe(key, fn) {
        if (!listeners[key]) {
            listeners[key] = [];
        }
        listeners[key].push(fn);

        // Return unsubscribe function
        return () => {
            listeners[key] = listeners[key].filter(cb => cb !== fn);
        };
    }

    function setPersistence(key, type, migrate = true) {
        if (type !== 'local' && type !== 'session' && type !== 'memory' && type !== null) return;
        if (typeof window === 'undefined') return;

        // Migrate data location
        if (migrate) {
            removeFromStorage(key);
        }

        persistence[key] = type;

        if (migrate) {
            saveToStorage(key, get(key));
        }

        if (persistence_location === 'local') {
            try {
                localStorage.setItem('persistence', JSON.stringify(persistence));
            } catch (error) {
                console.warn('Failed to save persistence to local storage. Switching to session.');
                persistence[key] = 'session';
            }
        }

        if (persistence_location === 'session') {
            try {
                sessionStorage.setItem('persistence', JSON.stringify(persistence));
            } catch (error) {
                console.warn('Failed to save persistence to session storage. Switching to memory.');
                persistence[key] = 'memory';
            }
        }
    }

    loadPersistence();
    loadState();

    return {
        clear,
        get,
        set,
        setPersistence,
        subscribe };
}

export const store = createStore(
    {
        auth: {
            logged_in: false,
            token: null,
            refresh_token: null,
            expires_in: 0,
            type: null,
        },
        consent: {
            chatbox: false,
        },
        currentTrack: null,
        ketv: {
            online: false,
        },
        playing: false,
        services: [],
        user: {},
    },
    {
        auth: 'session',
        user: 'session',
        services: 'session',
        consent: 'local',
    }
);
